import classNames from "classnames";
import React, { Ref } from "react";
import { ProgressIndicatorCircular } from "./progress-indicator-circular";
import { CSS_CLASSES } from "./progress-indicator-css-classes";
import { ProgressIndicatorLabel } from "./progress-indicator-label";
import { ProgressIndicatorLinear } from "./progress-indicator-linear";
import { ProgressIndicatorValueLabel } from "./progress-indicator-value-label";
import { ProgressIndicatorProps, ProgressIndicatorVariantWithValueLabelProps } from "./progress-indicator.types";

export const ProgressIndicator = React.memo(
  React.forwardRef(
    (
      {
        className: classNameProp,
        variant = "circular",
        color = "primary",
        size = "medium",
        label,
        labelPlacement = "top",
        value,
        showValueLabel,
      }: ProgressIndicatorProps,
      ref: Ref<HTMLDivElement>
    ) => {
      const className = classNames(
        CSS_CLASSES.PROGRESS_INDICATOR_ROOT,
        {
          [CSS_CLASSES.PROGRESS_INDICATOR_FULL_WIDTH]: variant === "linear",
          [CSS_CLASSES.PROGRESS_INDICATOR_LABEL_PLACEMENT_TOP]: labelPlacement === "top",
          [CSS_CLASSES.PROGRESS_INDICATOR_LABEL_PLACEMENT_RIGHT]: labelPlacement === "right",
          [CSS_CLASSES.PROGRESS_INDICATOR_LABEL_PLACEMENT_BOTTOM]: labelPlacement === "bottom",
          [CSS_CLASSES.PROGRESS_INDICATOR_LABEL_PLACEMENT_LEFT]: labelPlacement === "left",
          [CSS_CLASSES.PROGRESS_INDICATOR_LINEAR_LABEL_PLACEMENT_TOP_OR_BOTTOM]:
            variant === "linear" && (labelPlacement === "top" || labelPlacement === "bottom"),
        },
        classNameProp
      );

      return (
        <div
          ref={ref}
          tabIndex={0}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
          aria-valuetext={label}
          className={className}
        >
          {label && <ProgressIndicatorLabel label={label} labelPlacement={labelPlacement} size={size} color={color} />}
          {showValueLabel && size !== "small" ? (
            variant === "circular" ? (
              <ProgressIndicatorCircularWithValueLabel value={value} color={color} size={size} />
            ) : (
              <ProgressIndicatorLinearWithValueLabel value={value} color={color} size={size} />
            )
          ) : variant === "circular" ? (
            <ProgressIndicatorCircular value={value} color={color} size={size} />
          ) : (
            <ProgressIndicatorLinear value={value} color={color} size={size} />
          )}
        </div>
      );
    }
  )
);

ProgressIndicator.displayName = "ProgressIndicator";

const ProgressIndicatorCircularWithValueLabel = ({
  value,
  color,
  size,
}: ProgressIndicatorVariantWithValueLabelProps) => (
  <div className={CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR}>
    <ProgressIndicatorCircular value={value} size={size} color={color} />
    <ProgressIndicatorValueLabel
      value={value}
      size={size}
      className={classNames(CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_VALUE_LABEL, {
        [CSS_CLASSES.PROGRESS_INDICATOR_CIRCULAR_WHITE_VALUE_LABEL]: color === "white",
      })}
    />
  </div>
);

const ProgressIndicatorLinearWithValueLabel = ({ value, color, size }: ProgressIndicatorVariantWithValueLabelProps) => (
  <div className={CSS_CLASSES.PROGRESS_INDICATOR_LINEAR}>
    <ProgressIndicatorLinear value={value} size={size} color={color} />
    <ProgressIndicatorValueLabel
      value={value}
      size={size}
      className={classNames(CSS_CLASSES.PROGRESS_INDICATOR_LINEAR_VALUE_LABEL, {
        [CSS_CLASSES.PROGRESS_INDICATOR_LINEAR_MEDIUM_VALUE_LABEL]: size === "medium",
        [CSS_CLASSES.PROGRESS_INDICATOR_LINEAR_LARGE_VALUE_LABEL]: size === "large",
        [CSS_CLASSES.PROGRESS_INDICATOR_LINEAR_WHITE_VALUE_LABEL]: color === "white",
      })}
    />
  </div>
);
